import React from 'react'

import { observer } from 'mobx-react-lite'

import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Stack from '@mui/system/Stack'

import { Icon } from '@mxenabled/mx-icons'

import { InstitutionLogo, Text } from '@mxenabled/mxui'

import { Drawer } from '../../common'
import { useGlobalCopyStore } from '../../common'

interface HoldingDetail {
  id: string
  description: string
  qty: number
  holding: string
  costBasis: number
  rawCostBasis: number
  totalGainLoss: number
  gainLossAvailable: boolean
  gainLossBasis: number
  marketValue: number
}

interface InvestmentDetailsDrawerProps {
  accountHoldingsData: {
    accounts: any
    holdings: HoldingDetail[]
  }
  currentEditedCostBasis: string
  editingHoldingId: string | null
  handleCostBasisBlur: (id: string) => void
  handleCostBasisChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleCostBasisClick: (holdingId: string, currentCostBasis: number) => void
  handleCostBasisKeyDown: (event: React.KeyboardEvent<HTMLInputElement>, id: string) => void
  isOpen: boolean
  onClose: () => void
}

const InvestmentDetailsDrawer: React.FC<InvestmentDetailsDrawerProps> = ({
  accountHoldingsData,
  currentEditedCostBasis,
  editingHoldingId,
  handleCostBasisBlur,
  handleCostBasisClick,
  handleCostBasisChange,
  handleCostBasisKeyDown,
  isOpen,
  onClose,
}) => {
  const { investments: copy } = useGlobalCopyStore()
  const [expandedStates, setExpandedStates] = React.useState<Record<string, boolean>>({})

  const handleAccordionChange =
    (holdingId: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedStates((prevStates) => ({
        ...prevStates,
        [holdingId]: isExpanded,
      }))
    }

  return (
    <Drawer
      ariaLabelClose={copy.overview.close_investment_drawer}
      isOpen={isOpen}
      onClose={onClose}
      title={copy.overview.investment_details}
    >
      <Stack sx={{ gap: 8, p: 24 }}>
        <InstitutionLogo
          alt={accountHoldingsData.accounts.institutionName || ''}
          institutionGuid={accountHoldingsData.accounts.institution_guid || ''}
          size={64}
        />
        <Text sx={{ color: 'text.primary' }} variant="body2">
          {accountHoldingsData.accounts.memberName}
        </Text>
        <Text sx={{ color: 'text.primary' }} variant="h2">
          {accountHoldingsData.accounts.user_name || accountHoldingsData.accounts.name}
        </Text>

        <Stack sx={{ alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between' }}>
          <Text sx={{ color: 'text.primary', fontWeight: 600 }} variant="body1">
            {copy.overview.current_balance}
          </Text>

          <Text sx={{ color: 'text.primary', fontWeight: 600 }} variant="body1">
            {accountHoldingsData.accounts.balance?.toLocaleString('en-US', {
              currency: 'USD',
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
              style: 'currency',
            })}
          </Text>
        </Stack>
      </Stack>

      <Stack
        sx={{
          bgcolor: 'background.default',
          flexDirection: 'row',
          justifyContent: 'space-between',
          px: 24,
          py: 10,
        }}
      >
        <Text sx={{ color: 'text.primary', fontWeight: 700 }} variant="caption">
          {copy.overview.holding}
        </Text>
        <Text sx={{ color: 'text.primary', fontWeight: 700 }} variant="caption">
          {copy.overview.market_value}
        </Text>
      </Stack>
      {accountHoldingsData.holdings?.map((holding, index) => {
        const isCurrentlyEditing = editingHoldingId === holding.id
        return (
          <Accordion
            disableGutters={true}
            expanded={expandedStates[holding.id] === true}
            key={holding.id}
            onChange={holding ? handleAccordionChange(holding?.id) : () => {}}
            sx={{
              ':before': index === 0 ? { opacity: 0 } : {},
              '.MuiAccordionDetails-root': { p: 0 },
            }}
          >
            <AccordionSummary
              aria-controls="panel1a-content"
              id="panel1a-header"
              sx={[
                { height: 64 },
                expandedStates[holding.id] &&
                  index > 0 && {
                    borderTop: 1,
                    borderColor: 'divider',
                  },
              ]}
            >
              <Stack sx={{ flexDirection: 'row', width: '100%' }}>
                <div style={{ width: 24 }} />
                <Stack
                  sx={[
                    {
                      alignItems: 'center',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      pr: 24,
                      py: 14,
                      width: '100%',
                    },
                    expandedStates[holding.id] && {
                      borderBottom: 1,
                      borderColor: 'divider',
                    },
                  ]}
                >
                  <Stack
                    sx={{
                      alignItems: 'center',
                      flex: 1,
                      flexDirection: 'row',
                      gap: 12,
                      minWidth: 0,
                    }}
                  >
                    <Stack sx={{ minWidth: 0, overflow: 'hidden' }}>
                      <Text
                        sx={{
                          fontWeight: 600,
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 1,
                          overflow: 'hidden',
                          overflowWrap: 'anywhere',
                        }}
                        variant="body1"
                      >
                        {holding.holding}
                      </Text>
                      <Text
                        sx={{
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 2,
                          overflow: 'hidden',
                          overflowWrap: 'anywhere',
                        }}
                        variant="caption"
                      >
                        {holding.description}
                      </Text>
                    </Stack>
                  </Stack>

                  <Stack
                    sx={{
                      alignItems: 'center',
                      flexShrink: 0,
                      flexDirection: 'row',
                      gap: 12,
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Stack>
                      <Text sx={{ fontWeight: 600, textAlign: 'right' }} variant={'body1'}>
                        {holding.marketValue?.toLocaleString('en-US', {
                          currency: 'USD',
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                          style: 'currency',
                        })}
                      </Text>
                      <Stack sx={{ alignItems: 'center', gap: 2, flexDirection: 'row' }}>
                        {!holding.gainLossAvailable ? (
                          <Text sx={{ color: 'grey.700' }} variant="caption">
                            —
                          </Text>
                        ) : (
                          <>
                            {holding.totalGainLoss !== 0 && (
                              <Icon
                                name={holding.totalGainLoss >= 0 ? 'trending_up' : 'trending_down'}
                                size={12}
                                sx={{
                                  verticalAlign: 'middle',
                                  mr: 1,
                                  color: holding.totalGainLoss >= 0 ? 'success.main' : 'grey.700',
                                }}
                              />
                            )}
                            <Text
                              sx={{
                                color: holding.totalGainLoss <= 0 ? 'grey.700' : 'success.main',
                                fontWeight: holding.totalGainLoss <= 0 ? 400 : 700,
                              }}
                              variant="caption"
                            >
                              {holding.gainLossBasis
                                ? `${((holding.totalGainLoss / holding.gainLossBasis) * 100).toFixed(2)}%`
                                : '0.00%'}
                            </Text>
                            <Text sx={{ color: 'grey.700' }} variant="caption">
                              |
                            </Text>
                            <Text
                              sx={{
                                color: holding.totalGainLoss <= 0 ? 'grey.700' : 'success.main',
                                fontWeight: holding.totalGainLoss <= 0 ? 400 : 700,
                              }}
                              variant="caption"
                            >
                              {Math.abs(holding.totalGainLoss).toLocaleString('en-US', {
                                currency: 'USD',
                                maximumFractionDigits: 2,
                                minimumFractionDigits: 2,
                                style: 'currency',
                              })}
                            </Text>
                          </>
                        )}
                      </Stack>
                    </Stack>

                    <Icon
                      name={expandedStates[holding.id] ? 'expand_less' : 'expand_more'}
                      size={20}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack
                sx={{
                  alignItems: 'center',
                  bgcolor: 'background.default',
                  flexDirection: 'row',
                  pl: 24,
                }}
              >
                <Stack sx={{ width: 32 }} />
                <Stack sx={{ flex: 1 }}>
                  <Stack
                    sx={{
                      alignItems: 'center',
                      borderBottom: 1,
                      borderColor: 'divider',
                      flexDirection: 'row',
                      height: 48,
                      justifyContent: 'space-between',
                      pr: 24,
                    }}
                  >
                    <Text sx={{ color: 'text.primary' }} variant="body1">
                      {copy.overview.quantity}
                    </Text>
                    <Text sx={{ color: 'text.primary' }} variant="body2">
                      {holding.qty}
                    </Text>
                  </Stack>
                  <Stack
                    sx={{
                      alignItems: 'center',
                      flexDirection: 'row',
                      height: 48,
                      justifyContent: 'space-between',
                      pr: 24,
                    }}
                  >
                    <Text sx={{ color: 'text.primary' }} variant="body1">
                      {copy.overview.cost_basis_header}
                    </Text>

                    <Stack
                      direction="row"
                      sx={{
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      {isCurrentlyEditing ? (
                        <TextField
                          autoFocus={true}
                          onBlur={() => handleCostBasisBlur(holding.id)}
                          onChange={handleCostBasisChange}
                          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                            handleCostBasisKeyDown(e, holding.id)
                          }
                          size="small"
                          sx={{
                            fontSize: 14,
                            width: 58,
                            '& .MuiInputBase-input': {
                              padding: '4px 0',
                              textAlign: 'center',
                              fontWeight: 600,
                              color: 'text.primary',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                          value={currentEditedCostBasis}
                        />
                      ) : (
                        <Text sx={{ fontSize: 14, width: 58 }} variant="body2">
                          {holding.gainLossAvailable
                            ? holding.costBasis?.toLocaleString('en-US', {
                                currency: 'USD',
                                maximumFractionDigits: 2,
                                minimumFractionDigits: 2,
                                style: 'currency',
                              })
                            : '—'}
                        </Text>
                      )}

                      <IconButton
                        onClick={() => handleCostBasisClick(holding.id, holding.rawCostBasis)}
                        sx={{ minWidth: 16, minHeight: 16 }}
                      >
                        <Icon name={'stylus'} size={16} sx={{ color: 'grey.700' }} />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>
        )
      })}
    </Drawer>
  )
}

export default observer(InvestmentDetailsDrawer)
